import { useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { updatePreferences, usePreferences } from '../api/preferences'
import { useUIStore } from '../store/ui'

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    return ''
  }
}

/** Guards against re-PUTting the timezone on every remount within a session. */
let timezonePushed = false

/**
 * Reconciles server-stored preferences with the browser on mount.
 *
 * The theme is applied silently (server wins). The timezone goes the other
 * way: the browser is the only authority on it, and the reminder scheduler
 * needs it to turn a wall-clock "09:00" into an instant.
 *
 * Returns a mutation object so call sites can persist a user's new theme
 * without blocking the instant local apply.
 */
export function usePreferencesSync() {
  const setThemeId = useUIStore((s) => s.setThemeId)
  const qc = useQueryClient()
  const { data: prefs } = usePreferences()

  useEffect(() => {
    if (!prefs) return

    if (prefs.theme_id !== useUIStore.getState().themeId) {
      setThemeId(prefs.theme_id)
    }

    // 'Local' and '' are rejected by the server, so only push a real IANA name.
    const tz = browserTimezone()
    if (!timezonePushed && tz && tz !== 'Local' && prefs.timezone !== tz) {
      timezonePushed = true
      void updatePreferences({ timezone: tz })
        .then(() => qc.invalidateQueries({ queryKey: ['preferences'] }))
        .catch(() => {
          timezonePushed = false
        })
    }
  }, [prefs, setThemeId, qc])

  return useMutation({
    mutationFn: (id: string) => updatePreferences({ theme_id: id }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['preferences'] }),
  })
}
